
import { supabase } from './client';
import { Course, TeeBox, DbCourse, DbHole, Hole } from '@/types';
import { ExtendedCourse } from '@/types/courses';

/**
 * Convert a database course to the Course type used in the application
 */
export const dbCourseToCourse = (dbCourse: DbCourse & { holes?: DbHole[]; teeOptions?: TeeBox[] }): Course => {
  // Ensure we have an array of holes (empty if none provided)
  const holes: Hole[] = dbCourse.holes?.map((hole) => ({
    number: hole.hole_number,
    par: hole.par,
    handicap: hole.handicap || 0,
    yardage: {
      red: 0,
      gold: 0,
      white: 0,
      blue: 0,
      black: 0
    }
  })) || [];
  
  return {
    id: dbCourse.id,
    name: dbCourse.name,
    location: `${dbCourse.city || ''}, ${dbCourse.state || ''}`,
    // Use the teeOptions property if it exists, otherwise fallback to available_tees
    teeOptions: dbCourse.teeOptions || dbCourse.available_tees || [],
    holes: holes,
    holesCount: dbCourse.holes_count || 18,
    par: dbCourse.par || 72,
    imageUrl: dbCourse.image_url
  };
};

/**
 * Get all courses
 */
export const getCourses = async () => {
  const { data, error } = await supabase
    .from('courses')
    .select('*');

  if (error) {
    console.error('Error fetching courses:', error);
    return [];
  }

  // For each course, fetch its available tees using the get_course_tees function
  const coursesWithTees = await Promise.all(data.map(async (course) => {
    try {
      const { data: teeData, error: teeError } = await supabase
        .rpc('get_course_tees', { course_id: course.id });
      
      if (teeError) {
        console.error(`Error fetching tee options for course ${course.id}:`, teeError);
        // Add teeOptions property with fallback to available_tees
        return { ...course, teeOptions: course.available_tees || [] };
      }
      
      if (Array.isArray(teeData)) {
        // Add teeOptions property using the data from get_course_tees
        return { 
          ...course, 
          teeOptions: teeData.map((tee: { tee_color: TeeBox }) => tee.tee_color) 
        };
      }
      
      // Default fallback
      return { ...course, teeOptions: course.available_tees || [] };
    } catch (error) {
      console.error(`Error processing tee data for course ${course.id}:`, error);
      return { ...course, teeOptions: course.available_tees || [] };
    }
  }));

  return coursesWithTees || [];
};

/**
 * Get a course by ID
 */
export const getCourseById = async (courseId: string) => {
  try {
    // First get the basic course info
    const { data: course, error } = await supabase
      .from('courses')
      .select('*')
      .eq('id', courseId)
      .single();
    
    if (error) {
      console.error('Error fetching course:', error);
      return null;
    }
    
    if (!course) {
      return null;
    }
    
    // Add the teeOptions property to the course object
    const courseWithTeeOptions = { ...course, teeOptions: course.available_tees || [] };
    
    // Get the available tee options using our new function
    try {
      const { data: teeData, error: teeError } = await supabase
        .rpc('get_course_tees', { course_id: courseId });
      
      if (teeError) {
        console.error('Error fetching tee options:', teeError);
        // Fall back to the deprecated column if the function fails
      } else if (Array.isArray(teeData)) {
        courseWithTeeOptions.teeOptions = teeData.map((tee: { tee_color: TeeBox }) => tee.tee_color);
      }
    } catch (error) {
      console.error('Error processing tee data:', error);
    }
    
    // Now get the holes for this course
    const { data: holes, error: holesError } = await supabase
      .from('holes')
      .select('*')
      .eq('course_id', courseId)
      .order('hole_number');
    
    if (holesError) {
      console.error('Error fetching holes:', holesError);
      return null;
    }
    
    // Format the course with holes into the expected structure
    const formattedCourse = dbCourseToCourse({
      ...courseWithTeeOptions,
      holes: holes
    });
    
    return formattedCourse;
  } catch (error) {
    console.error('Error in getCourseById:', error);
    return null;
  }
};

/**
 * Create a new course
 */
export const createCourse = async (
  name: string,
  state: string,
  city: string,
  userId: string,
  availableTees: TeeBox[],
  holesCount?: number,
  par?: number,
  imageUrl?: string
) => {
  // We still store available_tees for backward compatibility
  const { data, error } = await supabase
    .from('courses')
    .insert([
      {
        name,
        state,
        city,
        user_id: userId,
        available_tees: availableTees, // Keep this for now for backward compatibility
        holes_count: holesCount,
        par: par,
        image_url: imageUrl
      },
    ])
    .select();

  if (error) {
    console.error('Error creating course:', error);
    return null;
  }

  if (data && data.length > 0) {
    // Add the teeOptions property to the returned course
    const courseWithTeeOptions = { ...data[0], teeOptions: availableTees };
    return dbCourseToCourse(courseWithTeeOptions);
  }
  
  return null;
};

/**
 * Update an existing course
 */
export const updateCourse = async (
  id: string,
  name?: string,
  state?: string,
  city?: string,
  availableTees?: TeeBox[],
  holesCount?: number,
  par?: number,
  imageUrl?: string
) => {
  const updates: {
    name?: string;
    state?: string;
    city?: string;
    available_tees?: TeeBox[];
    holes_count?: number;
    par?: number;
    image_url?: string;
  } = {};

  if (name !== undefined) updates.name = name;
  if (state !== undefined) updates.state = state;
  if (city !== undefined) updates.city = city;
  if (availableTees !== undefined) updates.available_tees = availableTees; // Keep for backwards compatibility
  if (holesCount !== undefined) updates.holes_count = holesCount;
  if (par !== undefined) updates.par = par;
  if (imageUrl !== undefined) updates.image_url = imageUrl;

  const { data, error } = await supabase
    .from('courses')
    .update(updates)
    .eq('id', id)
    .select();

  if (error) {
    console.error('Error updating course:', error);
    return null;
  }

  if (data && data.length > 0) {
    // Add teeOptions to the returned data
    const courseWithTeeOptions = { ...data[0], teeOptions: availableTees || data[0].available_tees || [] };
    return dbCourseToCourse(courseWithTeeOptions);
  }
  
  return null;
};

/**
 * Find a course by its GolfCourseAPI ID (for dedup on import)
 */
export const findCourseByApiId = async (apiCourseId: number): Promise<{ id: string; name: string } | null> => {
  const { data, error } = await supabase
    .from('courses')
    .select('id, name')
    .eq('api_course_id', apiCourseId)
    .maybeSingle();

  if (error) {
    console.error('Error finding course by API ID:', error);
    return null;
  }

  return data;
};

/**
 * Search courses by name (local DB search for already-imported courses)
 */
export const searchCoursesByName = async (query: string): Promise<Array<{ id: string; name: string; city: string; state: string; api_course_id: number | null }>> => {
  const { data, error } = await supabase
    .from('courses')
    .select('id, name, city, state, api_course_id')
    .ilike('name', `%${query}%`)
    .limit(10);

  if (error) {
    console.error('Error searching courses by name:', error);
    return [];
  }

  return data || [];
};

/**
 * Get courses that accepted friends have set as their home course.
 * Returns deduplicated list of {id, name, city, state}.
 */
export const getFriendCourses = async (
  userId: string
): Promise<Array<{ id: string; name: string; city: string; state: string }>> => {
  // 1. Get accepted friend IDs
  const { data: friendships, error: fErr } = await supabase
    .from('friendships')
    .select('user_id, friend_id')
    .eq('status', 'accepted')
    .or(`user_id.eq.${userId},friend_id.eq.${userId}`);

  if (fErr || !friendships?.length) return [];

  const friendIds = friendships.map(f =>
    f.user_id === userId ? f.friend_id : f.user_id
  );

  // 2. Get those friends' home_course_id (filter nulls, deduplicate)
  const { data: profiles, error: pErr } = await supabase
    .from('profiles')
    .select('home_course_id')
    .in('id', friendIds)
    .not('home_course_id', 'is', null);

  if (pErr || !profiles?.length) return [];

  const courseIds = [...new Set(profiles.map(p => p.home_course_id as string))];

  // 3. Get course details
  const { data: courses, error: cErr } = await supabase
    .from('courses')
    .select('id, name, city, state')
    .in('id', courseIds);

  if (cErr || !courses) return [];

  return courses;
};

/**
 * Delete a course
 */
export const deleteCourse = async (id: string) => {
  const { error } = await supabase
    .from('courses')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('Error deleting course:', error);
    return false;
  }

  return true;
};
